/**
 * One-door "Add a person" API client (POST /admin-review/people).
 *
 * Wraps the single endpoint that writes a staff_roster row and a
 * pending_grants invite together. Mirrors staffAdmin.ts / team.ts's
 * request/error shape. See api/src/functions/adminPeople.ts.
 */

import { writeFetch } from './writeFetch';
import type { HomePointOutcome, StaffHomeParts, StaffLinkRow } from './staffAdmin';
import type { UserTenantRole } from './types';
import { apiErrorFrom } from './api';

export interface CreatePersonRequest {
  full_name: string;
  billable_role: string;
  employment_type: 'employee' | 'contractor';
  email?: string;
  phone?: string;
  credentials?: string;
  unit_id?: string;
  /** OWNER ONLY — mirrors staffAdmin.ts CreateStaffRequest.company_id. */
  company_id?: string;
  /** Weekly hours target for any designation, as typed. */
  target_weekly_hours?: string;
  /** The employee-card directory fields — mirrors staffAdmin.ts
   *  CreateStaffRequest. Sent only when filled in. */
  birthday_month?: number;
  birthday_day?: number;
  work_anniversary?: string;
  start_date?: string;
  /** The home as its five parts; sent only when filled in. */
  home_address?: StaffHomeParts;
  personal_phone?: string;
  /** Defaults true server-side when absent; the form always sends it
   *  explicitly. false = the roster-only save (no invite fields needed). */
  grant_login: boolean;
  /** Required when grant_login is true. */
  access_role?: UserTenantRole;
  /** For a returning Directory Editor with care-team history: the number
   *  of clients the office confirms she loses access to, sent on the
   *  resubmission after the confirm. */
  end_care_teams?: number;
}

export interface PendingGrantSummary {
  email: string;
  role: UserTenantRole;
  display_name: string | null;
  created_at: string;
}

export interface CreatePersonResponse {
  row: StaffLinkRow;
  pending_grant: PendingGrantSummary | null;
  /** What the map did with a home address on the create. */
  home_point?: HomePointOutcome;
  request_id?: string;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const base = (slug: string): string => `/api/t/${encodeURIComponent(slug)}/v1/admin-review/people`;

export const peopleAdminApi = {
  /** Create the roster row and, when grant_login is true, the invite —
   *  one transaction on the server; both land or neither does. */
  async create(tenantSlug: string, input: CreatePersonRequest): Promise<CreatePersonResponse> {
    return (await requestJson(base(tenantSlug), {
      method: 'POST',
      body: JSON.stringify(input),
    })) as CreatePersonResponse;
  },
};

